import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { CirclePlus, X } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { QuickQuoteForm } from "@/components/forms/QuickQuoteForm";
import heroAsset from "@/assets/consultation-hero-2.webp";
import statsAsset from "@/assets/stats-swoosh.webp";
import harrisAsset from "@/assets/consultation-harris.webp";
import playAsset from "@/assets/gold-play-button.webp";
import harrisPortrait from "@/assets/consultation-harris-portrait.webp";
import maskAsset from "@/assets/mask-group-4-1-1.png.asset.json";
import logoAsset from "@/assets/restore-logo.png.asset.json";
import { absoluteUrl } from "@/lib/absolute-url";

const TITLE = "What to Expect at a Hair Transplant Consultation";
const DESCRIPTION =
  "Dr. Harris, the RESTORE Medical Director talks about what to expect during a hair transplant consultation that can be virtual or in person. Learn more!";
const URL =
  "https://restorehair.com/what-to-expect-at-your-hair-restoration-consultation/";

const HERO_BG = heroAsset;
const STATS_BG = statsAsset;
const HARRIS_IMG = harrisAsset;
const PLAY_BUTTON = playAsset;
const MASK_BG = maskAsset.url;
const HERO_SOCIAL_IMAGE =
  "/img/r/wp-2022-06-group-167.webp";
const VIDEO_URL =
  "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/restore_docbio_harris2022withpatients_v2(Original)_1CompressedForWebsite.mp4";

const stats = [
  { value: "18m", label: ["Total", "Grafts"] },
  { value: "25+", label: ["Years of", "Excellence"] },
  { value: "20k", label: ["Successful", "Procedures"] },
];

const locations = [
  {
    img: "/img/r/wp-2025-03-denver-map-img.webp",
    city: "Denver, CO",
    address: ["2301 Blake St", "Suite 113"],
    to: "/locations/restore-denver/",
  },
  {
    img: "/img/r/wp-2025-03-new-york-map-img.webp",
    city: "New York, NY",
    address: ["240 W 37th St 5th Floor"],
    to: "/locations/new-york/",
  },
  {
    img: "/img/r/wp-2025-03-chicago-map-img.webp",
    city: "Chicago, IL",
    address: ["159 N Sangamon St", "Suite 200"],
    to: "/locations/restore-chicago/",
  },
  {
    img: "/img/r/wp-2025-03-dallas-map-img.webp",
    city: "Dallas, TX",
    address: ["3108 Midway Rd", "Suite 202A"],
    to: "/locations/restore-dallas/",
  },
  {
    img: "/img/r/wp-2025-03-charlotte-map-img.webp",
    city: "Charlotte, NC",
    address: ["7725 Ballantyne Commons", "Parkway, Suite 101"],
    to: "/locations/restore-charlotte/",
  },
];

export const faqs = [
  {
    q: "What happens at a hair transplant consultation?",
    a: "During your consultation, our experienced consultant will evaluate your hair loss, discuss your concerns, and determine if you're a candidate for a hair transplant. You'll also learn about the process, including potential techniques like a No-Shave FUE Procedure.",
  },
  {
    q: "What should I expect at a hair transplant consultation?",
    a: "You can expect a thorough scalp examination, a discussion about your hair loss history, and a personalized treatment plan tailored to your goals. The consultant will also provide realistic expectations for results.",
  },
  {
    q: "How is a hair transplant consultation conducted?",
    a: "The consultation includes a detailed discussion with the doctor or consultant to understand your needs and explore the best options for hair restoration, including the latest technologies and procedures.",
  },
  {
    q: "How much does a hair transplant consultation cost?",
    a: "RESTORE Hair offers free consultation to help you understand your options. We will discuss our pricing details including understanding pricing by follicular unit.",
  },
  {
    q: "Why is a scalp examination important during a hair transplant consultation?",
    a: "The scalp examination helps the consultant evaluate hair density, the extent of hair loss, and the health of your donor area. This information is crucial for the doctor to develop a successful treatment plan.",
  },
  {
    q: "Can I set realistic goals for hair restoration during the consultation?",
    a: "Absolutely! Your consultant will work with you to establish achievable goals based on your hair type, degree of loss, and the results of similar procedures. These free consultations with our expert team are the best way to find out if a hair transplant is right for you.",
  },
  {
    q: "What is the RESTORE® No-Shave FUE™ procedure, and will it be discussed in the consultation?",
    a: "The No-Shave FUE hair transplant system is a state-of-the-art technique for precise follicle extraction. If you're a candidate for this type of hair transplant surgery, it will be explained during your consultation.",
  },
  {
    q: "How does RESTORE Hair ensure a personalized experience during the consultation?",
    a: "Our team focuses on understanding your unique needs, goals, and lifestyle to create a tailored approach to your hair restoration journey.",
  },
  {
    q: "How can I prepare for my hair transplant consultation?",
    a: "Come prepared with questions about the process, your expectations, and your medical history. You might also bring photos to help the consultant understand your desired results.",
  },
];

export const Route = createFileRoute(
  "/what-to-expect-at-your-hair-restoration-consultation",
)({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
       { property: "og:image", content: absoluteUrl(HERO_SOCIAL_IMAGE) },
       { property: "og:image:alt", content: "what to expect" },
      { name: "twitter:card", content: "summary_large_image" },
       { name: "twitter:image", content: absoluteUrl(HERO_SOCIAL_IMAGE) },
       { name: "twitter:image:alt", content: "what to expect" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: ConsultationPage,
});

/** Gold "+" accordion row matching the RESTORE toggle styling. */
function ToggleRow({
  title,
  open,
  onToggle,
  children,
}: {
  title: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-[15px] bg-background">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-6 px-6 py-[15px] text-left md:px-10"
      >
        <span className="text-lg font-semibold leading-snug tracking-[0.5px] text-[#666666] md:text-2xl md:leading-7">
          {title}
        </span>
        <span className="shrink-0 text-primary">
          <CirclePlus
            className={`h-8 w-8 transition-transform duration-200 md:h-10 md:w-10 ${
              open ? "rotate-45" : ""
            }`}
            strokeWidth={1.25}
          />
        </span>
      </button>
      {/* In the server HTML, hidden until opened. */}
      <div hidden={!open} className="px-6 pb-8 md:px-[45px]">{children}</div>
    </div>
  );
}

function ConsultationPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openSchedule, setOpenSchedule] = useState<number | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background font-sans">
      <Header logoSrc={logoAsset.url} />
      <main>
        {/* Hero - mobile */}
        <section className="bg-background px-5 pt-8 text-center md:hidden">
          <p className="text-[30px] font-bold leading-[1.2] text-foreground">
            What to expect
            <br />
            at your consultation
          </p>
          <p className="mt-3 text-base font-bold leading-snug text-foreground">
            Watch Dr. Harris&apos; Welcome video
          </p>
          <button
            type="button"
            onClick={() => setVideoOpen(true)}
            aria-label="Play Dr. Harris' welcome video"
            className="mx-auto mt-6 block"
          >
            <img src={PLAY_BUTTON} alt="" aria-hidden="true" className="h-16 w-16" />
          </button>
          <img
            src={harrisPortrait}
            alt="Dr. James Harris, RESTORE® Medical Director"
            className="mx-auto mt-4 block h-auto w-full max-w-[420px]"
          />
        </section>

        {/* Hero - desktop */}
        <section
          data-publish-image-check
          className="hidden bg-cover bg-top bg-no-repeat md:block"
          style={{ backgroundImage: `url("${HERO_BG}")` }}
        >
          <div className="mx-auto flex min-h-[430px] max-w-[1280px] items-center px-5 py-12 md:min-h-[627px] md:py-4">
            <div className="w-full md:max-w-[50%]">
              <button
                type="button"
                onClick={() => setVideoOpen(true)}
                aria-label="Play Dr. Harris' welcome video"
                className="block"
              >
                <img src={PLAY_BUTTON} alt="" aria-hidden="true" className="h-16 w-16" />
              </button>
              <h1 className="mt-6 text-[40px] font-bold leading-[1.2] text-foreground">
                What to expect
                <br />
                at a hair transplant consultation
              </h1>
              <p className="mt-4 text-[24px] font-normal leading-[1.4] text-foreground">
                Watch the Welcome video from the RESTORE® Medical Director and Hair Restoration
                Surgeon, Dr. James Harris.
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="hidden bg-background py-10 md:block md:py-14">
          <div className="mx-auto max-w-[1200px] px-5">
            <div
              className="overflow-hidden rounded-[20px] bg-cover bg-center bg-no-repeat px-6 py-[50px] md:px-10"
              style={{ backgroundImage: `url("${STATS_BG}")` }}
            >
              <div className="grid gap-10 text-left sm:grid-cols-3">
                {stats.map((s) => (
                  <div key={s.value} className="px-2 md:px-6">
                    <p className="m-0 text-[64px] font-bold leading-none text-foreground md:text-[88px]">
                      {s.value}
                    </p>
                    <p className="mt-4 text-[22px] leading-8 text-foreground md:text-[28px] md:leading-9">
                      {s.label[0]}
                      <br />
                      {s.label[1]}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Virtual consult CTA */}
        <section className="px-4 py-8 md:px-8 md:py-12">
          <div className="mx-auto grid max-w-6xl overflow-hidden md:grid-cols-2">
            <img
              src={HARRIS_IMG}
              alt="Dr. James Harris consulting with a RESTORE® patient"
              loading="lazy"
              data-publish-image-check
              className="h-full w-full object-cover object-left"
            />
            <div className="flex items-center bg-[#575757] px-6 py-10 md:px-12 md:py-14">
              <div className="max-w-[560px]">
                <h2 className="text-[26px] font-normal leading-[1.25] text-white md:text-[34px]">
                  Schedule Your Complimentary Virtual Consultation Today
                </h2>
                <h3 className="mt-6 text-base font-bold text-white md:text-lg">
                  We've Made The Process Even Easier
                </h3>
                <p className="mt-4 text-base leading-8 text-white md:text-lg">
                  From the comfort of your home, our RESTORE® Hair client advisors will explain how
                  the procedure works and create a customized treatment tailored for you specific
                  needs.
                </p>
                <p className="mt-4 text-base leading-8 text-white md:text-lg">
                  Want the details first?{" "}
                  <a
                    href="/downloads/restore_presentation_mobile_v26-1.pdf"
                    target="_blank"
                    rel="noopener"
                    className="font-semibold underline"
                  >
                    Download our free hair restoration brochure (PDF)
                  </a>
                  .
                </p>
                <Link
                  to="/schedule-a-consult/virtual/"
                  className="mt-8 inline-block bg-primary px-9 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground"
                >
                  Schedule Now
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Schedule accordion */}
        <section className="bg-[#f5f5f5] py-14 md:py-20">
          <div className="mx-auto max-w-[1200px] px-5">
            <h2 className="mb-10 text-center text-[30px] font-normal text-foreground md:text-[40px]">
              Schedule Your Consultation Today
            </h2>

            <ToggleRow
              title="In person Consultation"
              open={openSchedule === 0}
              onToggle={() => setOpenSchedule(openSchedule === 0 ? null : 0)}
            >
              <p className="text-base leading-8 text-[#6e6e6e]">
                At your in person consultation you will receive a complimentary hair and scalp
                analysis, to see what RESTORE can do for you. You'll learn about:
              </p>
              <ul className="mt-4 list-disc space-y-4 pl-6 text-base leading-8 text-[#6e6e6e]">
                <li>
                  <strong className="text-foreground">The RESTORE® F.U.E. Method.</strong> It's the
                  ultimate in discretion. You can get back to work and back to your life
                  immediately. No one will know you had a procedure unless you tell them.
                </li>
                <li>
                  <strong className="text-foreground">Payment plan options.</strong> It's one more
                  way we make it easier to look your best.
                </li>
              </ul>
            </ToggleRow>

            <ToggleRow
              title="Virtual Consultation"
              open={openSchedule === 1}
              onToggle={() => setOpenSchedule(openSchedule === 1 ? null : 1)}
            >
              <p className="text-base leading-8 text-[#6e6e6e]">
                For anyone who is out of state and you are unable to visit our office for an
                in-person consultation, a phone consultation can be arranged. We will ask you to
                send photos of your hair and scalp so that we can discuss the possible treatments
                for your unique situation.
              </p>
              <Link
                to="/schedule-a-consult/virtual/"
                className="mt-6 inline-block bg-primary px-8 py-4 text-sm font-bold uppercase tracking-wide text-primary-foreground"
              >
                Virtual Consultation
              </Link>
            </ToggleRow>

            <ToggleRow
              title="Locations"
              open={openSchedule === 2}
              onToggle={() => setOpenSchedule(openSchedule === 2 ? null : 2)}
            >
              <p className="text-base leading-8 text-[#6e6e6e]">
                RESTORE Hair is Conveniently located in Chicago, Charlotte, Dallas, Denver &amp; New
                York.
              </p>
              <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {locations.map((l) => (
                  <div key={l.city} className="bg-background">
                    <img
                      src={l.img}
                      alt={`Map of RESTORE® Hair ${l.city}`}
                      loading="lazy"
                      className="h-auto w-full object-cover"
                    />
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-foreground">{l.city}</h3>
                      {l.address.map((a) => (
                        <p key={a} className="text-sm leading-7 text-[#6e6e6e]">
                          {a}
                        </p>
                      ))}
                      <Link
                        to={l.to}
                        className="mt-4 inline-block text-sm font-semibold uppercase tracking-wide text-primary"
                      >
                        View More
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </ToggleRow>
          </div>
        </section>

        {/* FAQs */}
        <section className="bg-[#f5f5f5] pb-16 md:pb-24">
          <div className="mx-auto max-w-[1200px] px-5">
            <h2 className="mb-10 text-center text-[30px] font-normal text-foreground md:text-[40px]">
              Hair Transplant Consultation FAQs
            </h2>
            {faqs.map((f, i) => (
              <ToggleRow
                key={f.q}
                title={f.q}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <p className="text-base leading-8 text-[#6e6e6e]">
                  {f.q === "How much does a hair transplant consultation cost?" ? (
                    <>
                      RESTORE Hair offers free consultation to help you understand your options. We
                      will discuss our{" "}
                      <Link to="/pricing/" className="text-primary underline">
                        pricing details
                      </Link>{" "}
                      including understanding pricing by follicular unit.
                    </>
                  ) : (
                    f.a
                  )}
                </p>
              </ToggleRow>
            ))}
          </div>
        </section>

        <section className="bg-[#3f3f3f] py-12 md:py-16">
          <div className="mx-auto grid max-w-[1200px] items-start gap-10 px-5 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <div
              className="bg-cover bg-center py-4 text-white"
              style={{ backgroundImage: `linear-gradient(rgba(63,63,63,.82), rgba(63,63,63,.82)), url(${MASK_BG})` }}
            >
              <h2 className="text-[34px] font-bold leading-tight md:text-[42px]">
                Get a Quote
                <br />
                &amp; Save $1,000 on Your FUE Procedure.
              </h2>
              <p className="mt-7 max-w-lg text-base leading-8 text-white/90">
                Exact cost depends on your level of hair loss. Tell us about your hair loss to get a
                quick quote. A full head of hair pays off for a lifetime.
              </p>
              <p className="mt-5 max-w-lg text-xs italic leading-6 text-white/70">
                $1,000 off full price. Cannot be combined with any other discounts or offers.
              </p>
            </div>
            <div>
              <div className="mb-5 text-center text-white">
                <h2 className="text-[30px] font-bold leading-tight md:text-[38px]">
                  Get a quick quote. <span className="font-light italic">It&apos;s easy.</span>
                </h2>
                <p className="mt-2 text-base">Give us a minute, we&apos;ll give you the world.</p>
              </div>
              <QuickQuoteForm />
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {videoOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setVideoOpen(false)}
        >
          <button
            type="button"
            aria-label="Close video"
            onClick={() => setVideoOpen(false)}
            className="absolute right-5 top-5 text-white"
          >
            <X className="h-8 w-8" />
          </button>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <video
            src={VIDEO_URL}
            controls
            autoPlay
            className="max-h-[80vh] w-full max-w-4xl bg-black"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
